import type { TableRowView } from "../lib/api";
import { TableScroll } from "./TableScroll";

/**
 * The rows Claude read from this page's table, under the page they were read from. Columns are
 * the page's own headings. A value the OCR placed on the page is a button: pressing it draws its
 * box over the page. A value with no box is plain text, because a box is never invented. A row
 * whose tag was not found on the page is shown, flagged, and never reached the register.
 */
export function TableRowsPanel({ rows, cell, onCell }: {
  rows: TableRowView[]; cell: string | null; onCell: (cell: string | null) => void;
}) {
  const headers = Array.from(new Set(rows.flatMap((r) => r.cells.map((c) => c.header))));
  let section: string | null = null;
  return (
    <section className="card docview-rows" aria-label="Rows read from this table">
      <h3 style={{ fontSize: 15, marginBottom: 4 }}>Rows read from this table</h3>
      <p className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
        Read by Claude, checked against the page. Press a value to mark where it is printed.
      </p>
      <TableScroll label="Rows read from this table, scrollable">
        <table className="docview-rows-table">
          <thead>
            <tr>{headers.map((h) => <th key={h} scope="col">{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.flatMap((r) => {
              const out = [];
              if (r.row_class && r.row_class !== section) {
                section = r.row_class;
                out.push(
                  <tr key={`${r.id}-section`} className="docview-rows-section">
                    <th scope="colgroup" colSpan={headers.length}>{r.row_class}</th>
                  </tr>,
                );
              }
              const flagged = r.status !== "verified";
              out.push(
                <tr key={r.id} className={flagged ? "flagged" : undefined}>
                  {headers.map((h) => {
                    const c = r.cells.find((x) => x.header === h);
                    if (!c || !c.value) return <td key={h} />;
                    const id = `${r.id}:${h}`;
                    const note = flagged && c.field === "tag"
                      ? <span className="pill fail" style={{ marginLeft: 4 }}>tag not found on this page</span>
                      : null;
                    if (!c.box) {
                      return <td key={h} className={c.field === "tag" ? "mono" : undefined}>{c.value}{note}</td>;
                    }
                    return (
                      <td key={h}>
                        <button type="button" className="ghost docview-cell" aria-pressed={cell === id}
                                aria-label={`Show ${h} ${c.value} of ${r.tag} on the page`}
                                title={c.coverage < 1 ? `matched ${Math.round(c.coverage * 100)}% of the characters on the page` : undefined}
                                onClick={() => onCell(cell === id ? null : id)}>
                          {c.value}
                        </button>
                        {note}
                      </td>
                    );
                  })}
                </tr>,
              );
              return out;
            })}
          </tbody>
        </table>
      </TableScroll>
    </section>
  );
}
